import { z } from '@customerio/hydra-validation';
import type { ExperimentsSnapshotItem } from '@customerio/hydra-ui-api';
import type { SimulatedExposures } from './exposures';
import { SIM_KINDS, SIM_PROFILE_IDS, type ExperimentPatch, type Scenario, type SimCommand } from './scenario';

/*
 * The simulator's shared contract with the Ember app (customerio/ui). Hydra owns the engine and is
 * the only writer; Ember, running inside the Hydra shell on the same origin, reads the same storage
 * and listens on the same channel. Changing anything here is a breaking change for Ember's reader.
 *
 * Storage
 *   key    `cio.experiment-sim.v2.<workspaceId>.<experimentId>`
 *   value  JSON `StoredSimulation` (below):
 *     version     2
 *     scenario    engine-owned; readers must not interpret it
 *     patch       fields to shallow-merge over the raw experiment GET response, snake_case as the API
 *                 sends them (status, started_at, latest_results, exposures_by_variant, exposed_total,
 *                 winner_variant_id, ...). Readers apply `{ ...raw, ...patch }`.
 *     computedAt  ms epoch when `patch` was built; the host rebuilds it at least once a minute and on
 *                 every change, so "day N is today" stays true
 *     campaignIDs campaigns the experiment is attached to (not detached), as strings
 *     live        { status, winnerVariantId, isApplied, isDraft } for campaign and canvas overrides
 *     snapshots   the snapshots list exactly as GET .../experiments/:id/snapshots returns it, simulated
 *                 history rows included (optional for readers; Hydra always writes it)
 *     exposures   the GET .../experiments/:id/exposures response for the default query (no filters,
 *                 page 1 of 10), `series` and totals included (optional for readers; Hydra always writes it)
 *   Readers with no engine return the stored snapshots and exposures instead of calling the API.
 *   While an experiment is simulated, config saves (experiment update) are blocked.
 *   The prototype's v1 keys are ignored.
 *
 * Channel: `BroadcastChannel('cio-experiment-sim')` plus a same-window `CustomEvent('cio-experiment-sim')`
 * (a channel never delivers to its own window). Ids in messages Hydra sends are strings.
 *   { type: 'changed', workspaceId, experimentId }       a simulation was written or cleared
 *   { type: 'command', requestId, workspaceId, experimentId, body }
 *                                                        Ember asks the engine to run a lifecycle command
 *                                                        (`body` as the transitions endpoint takes it, or
 *                                                        `{ command: 'apply_winner' }`) instead of the API
 *   { type: 'command-result', requestId, ok, error? }    the engine's reply; Ember waits up to 5 s
 * Per-window UI messages (acted on only from the same-window event, never from the channel):
 *   { type: 'toolbar-host', present }                    Ember's workflow toolbar mounted (true) or
 *                                                        unmounted (false) its Simulator item; while
 *                                                        present the bottom-left launcher hides. Hydra
 *                                                        answers with `dock-state`.
 *   { type: 'toggle-dock', anchor: { left, top, width, height } }
 *                                                        the Simulate item was clicked; `anchor` is its
 *                                                        rect in the TOP window's viewport. Ember runs in
 *                                                        this document, so its getBoundingClientRect() is
 *                                                        already that; if Ember ever runs in a frame, it
 *                                                        must add the frame's offset before sending. Hydra
 *                                                        opens or closes the controls just above it.
 *   { type: 'dock-anchor', anchor: { left, top, width, height } }
 *                                                        the open item moved (e.g. the action side panel
 *                                                        shifted the toolbar); same coordinates as
 *                                                        `toggle-dock`. Hydra moves the open controls to
 *                                                        it and never opens or closes them for it.
 *   { type: 'focus-experiment', experimentId }           Ember → Hydra when an experiment action's panel
 *                                                        opens in the workflow builder: the dock shows
 *                                                        that experiment. Hydra forgets it when the
 *                                                        toolbar host goes away.
 *   { type: 'dock-state', open }                         Hydra → Ember after every open and close,
 *                                                        dispatched on this window and on every
 *                                                        same-origin child frame's window.
 *   { type: 'gate', open }                               Hydra → Ember whenever Hydra's gate opens or
 *                                                        closes, dispatched the same way.
 *
 * Gate: Hydra publishes its gate on its window, always, even while closed:
 *   window.__cioExperimentSimulator = { gateOpen: boolean }
 * Absent or false means closed. Readers must require BOTH their own gate and
 * `window.__cioExperimentSimulator?.gateOpen === true` before reading entries or intercepting
 * anything; otherwise they use the real API and show nothing simulated.
 *
 * Commands: an intercepted command never falls back to the real API. A `command` that gets no
 * `command-result` within 5 s, or one with `ok: false`, is an error the reader shows; it sends
 * nothing to the backend. `ok: false` with "This experiment isn't simulated…" means the reader's
 * entry is stale (cleared, or written for another workspace): it should re-read the store.
 */

const KEY_PREFIX = 'cio.experiment-sim.v2';
export const SIM_CHANNEL = 'cio-experiment-sim';

type Id = string | number;

export type SimulationLive = {
  status: string;
  winnerVariantId: string | null;
  isApplied: boolean;
  isDraft: boolean;
};

export type StoredSimulation = {
  version: 2;
  scenario: Scenario;
  patch: ExperimentPatch;
  computedAt: number;
  campaignIDs: string[];
  live: SimulationLive;
  snapshots: ExperimentsSnapshotItem[];
  exposures: SimulatedExposures;
};

const ScenarioSchema = z.object({
  version: z.literal(1),
  preset: z.string(),
  kind: z.enum(SIM_KINDS),
  profile: z.enum(SIM_PROFILE_IDS),
  day: z.number(),
  startAt: z.number().optional(),
  draft: z.boolean().optional(),
  durationDays: z.number().optional(),
  pausedAtDay: z.number().nullish(),
  stoppedAtDay: z.number().nullish(),
  completedAtDay: z.number().nullish(),
  winnerVariantId: z.string().nullish(),
  decidedEarly: z.boolean().optional(),
  applied: z.boolean().optional(),
});

// Any record, as the contract says: readers needing status or winner use `live`, so Hydra and
// Ember accept and reject the same entries.
const StoredPatchSchema = z.looseObject({});

const StoredSimulationSchema = z.object({
  version: z.literal(2),
  scenario: ScenarioSchema,
  patch: StoredPatchSchema,
  computedAt: z.number(),
  campaignIDs: z.array(z.string()),
  live: z.object({
    status: z.string(),
    winnerVariantId: z.string().nullable(),
    isApplied: z.boolean(),
    isDraft: z.boolean(),
  }),
  snapshots: z.array(z.looseObject({})).optional(),
  exposures: z.looseObject({}).optional(),
});

/** A stored simulation as read back: the patch is only known to carry what readers rely on. */
export type ReadSimulation = z.infer<typeof StoredSimulationSchema>;

export function simulationKey(workspaceId: Id, experimentId: Id): string {
  return `${KEY_PREFIX}.${workspaceId}.${experimentId}`;
}

function parseStored(raw: string | null): ReadSimulation | null {
  if (raw == null) {
    return null;
  }
  try {
    const parsed = StoredSimulationSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function storageItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** The stored simulation, ungated. Callers outside this module go through the gated readers in `reads.ts`. */
export function readStoredSimulation(workspaceId: Id, experimentId: Id): ReadSimulation | null {
  return parseStored(storageItem(simulationKey(workspaceId, experimentId)));
}

export function writeStoredSimulation(workspaceId: Id, experimentId: Id, value: StoredSimulation): void {
  window.localStorage.setItem(simulationKey(workspaceId, experimentId), JSON.stringify(value));
}

export function removeStoredSimulation(workspaceId: Id, experimentId: Id): void {
  window.localStorage.removeItem(simulationKey(workspaceId, experimentId));
}

function storageKeys(): string[] {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index++) {
      keys.push(window.localStorage.key(index) ?? '');
    }
    return keys;
  } catch {
    return [];
  }
}

/** Every experiment id with a stored simulation in the workspace. */
export function storedExperimentIds(workspaceId: Id): string[] {
  const prefix = `${KEY_PREFIX}.${workspaceId}.`;
  return storageKeys()
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
}

/* Channel ------------------------------------------------------------------------------------ */

const IdSchema = z.union([z.string(), z.number()]).transform(String);

const CommandBodySchema = z.object({
  command: z.string(),
  variant_id: z.string().nullish(),
  early: z.boolean().nullish(),
  enrollment_ends_at: z.number().nullish(),
});

const SimMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('changed'), workspaceId: IdSchema, experimentId: IdSchema }),
  z.object({
    type: z.literal('command'),
    requestId: z.string(),
    workspaceId: IdSchema,
    experimentId: IdSchema,
    body: CommandBodySchema,
  }),
  z.object({
    type: z.literal('command-result'),
    requestId: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
  }),
  // Per-window UI messages between Ember's workflow toolbar and this window's dock.
  z.object({ type: z.literal('toolbar-host'), present: z.boolean() }),
  z.object({
    type: z.literal('toggle-dock'),
    anchor: z.object({ left: z.number(), top: z.number(), width: z.number(), height: z.number() }),
  }),
  z.object({
    type: z.literal('dock-anchor'),
    anchor: z.object({ left: z.number(), top: z.number(), width: z.number(), height: z.number() }),
  }),
  z.object({ type: z.literal('dock-state'), open: z.boolean() }),
  z.object({ type: z.literal('gate'), open: z.boolean() }),
  z.object({ type: z.literal('focus-experiment'), experimentId: IdSchema }),
]);

export type SimMessage = z.infer<typeof SimMessageSchema>;
export type SimCommandMessage = Extract<SimMessage, { type: 'command' }>;
export type DockAnchorRect = Extract<SimMessage, { type: 'toggle-dock' }>['anchor'];

/** Where a message came from: another tab (channel) or this window (event). */
export type SimMessageSource = 'channel' | 'window';

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  channel ??= new BroadcastChannel(SIM_CHANNEL);
  return channel;
}

export function postSimMessage(message: SimMessage): void {
  getChannel()?.postMessage(message);
  // Listeners in this window never receive this channel's own posts.
  window.dispatchEvent(new CustomEvent(SIM_CHANNEL, { detail: message }));
}

function sameOriginFrameWindows(): Window[] {
  return Array.from(document.querySelectorAll('iframe')).flatMap((frame) => {
    try {
      // Reading the document throws for a cross-origin frame; only same-origin frames hear us.
      return frame.contentDocument == null || frame.contentWindow == null ? [] : [frame.contentWindow];
    } catch {
      return [];
    }
  });
}

/**
 * Posts a per-window message (`dock-state`, `gate`): to this window and to every same-origin child frame
 * (the embedded Ember app), never over the channel, so other tabs don't react to it.
 */
export function postWindowMessage(message: Extract<SimMessage, { type: 'dock-state' | 'gate' }>): void {
  for (const target of [window, ...sameOriginFrameWindows()]) {
    target.dispatchEvent(new CustomEvent(SIM_CHANNEL, { detail: message }));
  }
}

export function parseSimMessage(data: unknown): SimMessage | null {
  const parsed = SimMessageSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

export function subscribeSimMessages(onMessage: (message: SimMessage, source: SimMessageSource) => void): () => void {
  const deliver = (data: unknown, source: SimMessageSource) => {
    const message = parseSimMessage(data);
    if (message != null) {
      onMessage(message, source);
    }
  };
  const fromChannel = (event: MessageEvent) => {
    deliver(event.data, 'channel');
  };
  // Ember builds its events in its own frame's realm, so `instanceof CustomEvent` would miss them.
  const fromWindow = (event: Event) => {
    if ('detail' in event) {
      deliver(event.detail, 'window');
    }
  };
  const current = getChannel();
  current?.addEventListener('message', fromChannel);
  window.addEventListener(SIM_CHANNEL, fromWindow);
  return () => {
    current?.removeEventListener('message', fromChannel);
    window.removeEventListener(SIM_CHANNEL, fromWindow);
  };
}

export function postChanged(workspaceId: Id, experimentId: Id): void {
  postSimMessage({ type: 'changed', workspaceId: String(workspaceId), experimentId: String(experimentId) });
}

export type { SimCommand };

/** Closes the shared channel; the next post or subscription opens a fresh one. */
export function closeSimChannel(): void {
  channel?.close();
  channel = null;
}
